import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import {
  FormActiveFeaturesComponent,
  ActiveFeaturesForm,
} from './forms/form-active-features/form-active-features.component';
import {
  FormConnectionModeComponent,
  ConnectionModeForm,
} from './forms/form-connection-mode/form-connection-mode.component';
import {
  FormConfig1733Component,
  Config1733Form,
} from './forms/form-config-1733/form-config-1733.component';
import {
  FormBeoogoComponent,
  BeoogoForm,
} from './forms/form-beoogo/form-beoogo.component';
import {
  FormEformComponent,
  EFormForm,
} from './forms/form-eform/form-eform.component';
import { ApplicationSettingsService } from '../../../../../../lib/services/application-settings.service';
import { ApplicationSettings, Group } from '@icure/api';
import { MatSnackBar } from '@angular/material/snack-bar';

const JAVA_TYPE = 'org.taktik.icure.entities.ApplicationSettings';

const APP_SETTINGS_LABELS: Record<string, string> = {
  'APPSETTINGS|1733-CONFIG|1.0.0': 'Configuration 1733',
  'APPSETTINGS|ACTIVE-FEATURES|1.0.0': 'Module activé',
  'APPSETTINGS|BEOOGO|PRD|1.0.1': 'Beoogo',
  'APPSETTINGS|CONNECTION-MODE|1.0.0': 'Mode de connexion',
  'APPSETTINGS|EFORM|PRD|1.0.0': 'eForm',
};

interface AppSettingsPayload {
  _id: string;
  settings: Record<string, string>;
  java_type: string;
  rev_history: Record<string, never>;
}

@Component({
  selector: 'ms-app-settings-importation-dialog',
  imports: [
    FormActiveFeaturesComponent,
    FormConnectionModeComponent,
    FormConfig1733Component,
    FormBeoogoComponent,
    FormEformComponent
],
  templateUrl: './app-settings-importation-dialog.component.html',
  styleUrl: './app-settings-importation-dialog.component.scss',
})
export class AppSettingsImportationDialogComponent {
  public appSettingsId: string;
  public appSettingsLabel: string;
  public isFormValid = true;
  public initialActiveFeaturesForm: ActiveFeaturesForm = {
    onlineAgenda: false,
    patientApp: false,
    ivgModule: false,
    kineModule: false,
    bubblelink: false,
  };
  private currentFormData:
    | ActiveFeaturesForm
    | ConnectionModeForm
    | Config1733Form
    | BeoogoForm
    | EFormForm
    | null = null;
  private currentAppSettings: ApplicationSettings | undefined;

  constructor(
    public dialogRef: MatDialogRef<AppSettingsImportationDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      appSettingsId: string;
      selectedGroup: Group;
      currentAppSettings?: ApplicationSettings;
    },
    private applicationSettingsService: ApplicationSettingsService,
    private snackBar: MatSnackBar,
  ) {
    this.dialogRef.disableClose = true;
    this.appSettingsId = data.appSettingsId;
    this.appSettingsLabel =
      APP_SETTINGS_LABELS[this.appSettingsId] ?? this.appSettingsId;
    this.currentAppSettings = data.currentAppSettings;
    this.initialActiveFeaturesForm = this.buildActiveFeaturesForm();
  }

  public onFormChange(
    formData:
      | ActiveFeaturesForm
      | ConnectionModeForm
      | Config1733Form
      | BeoogoForm
      | EFormForm,
  ) {
    this.currentFormData = formData;
  }

  public onValidChange(valid: boolean) {
    this.isFormValid = valid;
  }

  private buildActiveFeaturesForm(): ActiveFeaturesForm {
    const settings = this.currentAppSettings?.settings ?? {};
    return {
      onlineAgenda: settings?.['onlineAgenda'] === 'true',
      patientApp: settings?.['patientApp'] === 'true',
      ivgModule: settings?.['abortion'] === 'true',
      kineModule: settings?.['physiotherapist'] === 'true',
      bubblelink: settings?.['hasBubblelinkAccess'] === 'true',
    };
  }

  public closeDialog() {
    this.dialogRef.close();
  }

  public async save() {
    if (!this.isFormValid) return;
    const json = this.buildAppSettingsJson();
    const groupId: string = this.data.selectedGroup?.id ?? '';
    const groupPassword: string = this.data.selectedGroup?.password ?? '';
    const cluster: string = this.data.selectedGroup?.servers?.[0]?.replace(
      'https://',
      '',
    )?.replace('.icure.cloud', '')?.replace(':444', '') ?? '';
    const response =
      await this.applicationSettingsService.createApplicationSettings(
        groupId,
        groupPassword,
        cluster,
        json,
      );
    if (response?.status?.status === 200 || response?.status?.status === 201) {
      this.snackBar.open(
        "L'application settings a bien été ajouté dans la db.",
        '',
        {
          duration: 5000,
          verticalPosition: 'top',
          horizontalPosition: 'center',
          panelClass: ['success'],
        },
      );
      this.dialogRef.close(true);
    } else {
      this.snackBar.open(
        "Erreur lors de l'ajout de l'application settings.",
        '',
        {
          duration: 5000,
          verticalPosition: 'top',
          horizontalPosition: 'center',
          panelClass: ['error'],
        },
      );
    }
  }

  private buildAppSettingsJson(): AppSettingsPayload | null {
    switch (this.appSettingsId) {
      case 'APPSETTINGS|EFORM|PRD|1.0.0':
        return this.buildEFormJson(this.currentFormData as EFormForm);
      case 'APPSETTINGS|BEOOGO|PRD|1.0.1':
        return this.buildBeoogoJson(this.currentFormData as BeoogoForm);
      case 'APPSETTINGS|1733-CONFIG|1.0.0':
        return this.buildConfig1733Json(this.currentFormData as Config1733Form);
      case 'APPSETTINGS|ACTIVE-FEATURES|1.0.0':
        return this.buildActiveFeaturesJson(
          this.currentFormData as ActiveFeaturesForm,
        );
      case 'APPSETTINGS|CONNECTION-MODE|1.0.0':
        return this.buildConnectionModeJson(
          this.currentFormData as ConnectionModeForm,
        );
      default:
        return null;
    }
  }

  private buildEFormJson(form: EFormForm): AppSettingsPayload {
    const env = form.isProduction ? 'PRD' : 'ACC';
    return {
      _id: `APPSETTINGS|EFORM|${env}|1.0.0`,
      settings: {
        'eform.host': form.host,
        'eform.grantType': form.grantType,
        'eform.integrationVersion': form.integrationVersion,
        'eform.userName': form.userName,
        'eform.password': form.password,
      },
      java_type: JAVA_TYPE,
      rev_history: {},
    };
  }

  private buildBeoogoJson(form: BeoogoForm): AppSettingsPayload {
    const env = form.isProduction ? 'PRD' : 'ACC';
    return {
      _id: `APPSETTINGS|BEOOGO|${env}|1.0.0`,
      settings: {
        'beoogo.host': form.host,
        'beoogo.ssoHost': form.ssoHost,
        'beoogo.patientApi': form.patientApi,
        'beoogo.patientUrl': form.patientUrl,
        'beoogo.ssoApi': form.ssoApi,
        'beoogo.clientId': form.clientId,
        'beoogo.clientSecret': form.clientSecret,
        'beoogo.userName': form.userName,
        'beoogo.password': form.password,
      },
      java_type: JAVA_TYPE,
      rev_history: {},
    };
  }

  private buildConfig1733Json(form: Config1733Form): AppSettingsPayload {
    return {
      _id: 'APPSETTINGS|1733-CONFIG|1.0.0',
      settings: {
        '1733.token': form.token,
      },
      java_type: JAVA_TYPE,
      rev_history: {},
    };
  }

  private buildActiveFeaturesJson(form: ActiveFeaturesForm): AppSettingsPayload {
    const currentSettings = this.currentAppSettings?.settings ?? {};
    return {
      _id: 'APPSETTINGS|ACTIVE-FEATURES|1.0.0',
      settings: {
        ...currentSettings,
        onlineAgenda: String(form.onlineAgenda),
        patientApp: String(form.patientApp),
        abortion: String(form.ivgModule),
        physiotherapist: String(form.kineModule),
        hasBubblelinkAccess: String(form.bubblelink),
        displayGroupName: String(
          currentSettings?.['displayGroupName'] ?? 'true',
        ),
      },
      java_type: JAVA_TYPE,
      rev_history: {},
    };
  }

  private buildConnectionModeJson(form: ConnectionModeForm): AppSettingsPayload {
    const modeMap: Record<string, string> = {
      cabinet: 'office',
      maisonMedicale: 'medicalHouse',
      pmg: 'guardPost',
    };
    return {
      _id: 'APPSETTINGS|CONNECTION-MODE|1.0.0',
      settings: {
        connectionMode: modeMap[form.mode] ?? '',
        guardPost_1733: String(form.is1733),
        is1733Flux: String(form.is1733),
      },
      java_type: JAVA_TYPE,
      rev_history: {},
    };
  }
}
